import type { EpisodeRecord, EpisodeKind, EpisodeOutcome } from '../core/types.js';
import { uuid } from '../core/ids.js';
import { nowIso } from '../core/time.js';
import { SCHEMA_VERSION, episodes } from '../storage/db.js';

export interface StartEpisodeOptions {
  habitId: string;
  planId?: string | undefined;
  methodId?: string | undefined;
  kind?: EpisodeKind | undefined;
  targetWaitMs?: number | undefined;
  triggers?: string[] | undefined;
  intensityStart?: 1 | 2 | 3 | 4 | 5 | undefined;
}

export async function startEpisode(opts: StartEpisodeOptions): Promise<EpisodeRecord> {
  const now = nowIso();
  const ep: EpisodeRecord = {
    id: uuid(),
    schemaVersion: SCHEMA_VERSION,
    habitId: opts.habitId,
    planId: opts.planId,
    methodId: opts.methodId,
    kind: opts.kind ?? 'urge',
    startedAt: now,
    targetWaitMs: opts.targetWaitMs,
    originalTargetWaitMs: opts.targetWaitMs,
    triggers: opts.triggers,
    intensityStart: opts.intensityStart,
    linkedEventIds: [],
    totalModuleMs: 0,
    modulesCompleted: 0,
    rescueSessionIds: [],
    createdAt: now,
    updatedAt: now,
  };
  await episodes.put(ep);
  return ep;
}

export async function getEpisode(id: string): Promise<EpisodeRecord | undefined> {
  return episodes.get(id);
}

export async function getActiveEpisode(habitId: string): Promise<EpisodeRecord | undefined> {
  return episodes.activeForHabit(habitId);
}

export async function linkEventToEpisode(episodeId: string, eventId: string): Promise<EpisodeRecord | undefined> {
  const ep = await episodes.get(episodeId);
  if (!ep) return undefined;
  if (ep.linkedEventIds.includes(eventId)) return ep;
  const updated: EpisodeRecord = {
    ...ep,
    linkedEventIds: [...ep.linkedEventIds, eventId],
    updatedAt: nowIso(),
  };
  await episodes.put(updated);
  return updated;
}

export async function updateEpisode(
  episodeId: string,
  patch: Partial<Omit<EpisodeRecord, 'id' | 'schemaVersion' | 'createdAt'>>,
): Promise<EpisodeRecord | undefined> {
  const ep = await episodes.get(episodeId);
  if (!ep) return undefined;
  const updated: EpisodeRecord = { ...ep, ...patch, updatedAt: nowIso() };
  await episodes.put(updated);
  return updated;
}

export async function recordRescueOnEpisode(
  episodeId: string,
  rescueSessionId: string,
  durationMs: number,
  completed: boolean,
): Promise<EpisodeRecord | undefined> {
  const ep = await episodes.get(episodeId);
  if (!ep) return undefined;
  const updated: EpisodeRecord = {
    ...ep,
    rescueSessionIds: [...(ep.rescueSessionIds ?? []), rescueSessionId],
    totalModuleMs: (ep.totalModuleMs ?? 0) + durationMs,
    modulesCompleted: (ep.modulesCompleted ?? 0) + (completed ? 1 : 0),
    updatedAt: nowIso(),
  };
  await episodes.put(updated);
  return updated;
}

export async function closeEpisode(
  episodeId: string,
  outcome: EpisodeOutcome,
  intensityEnd?: 1 | 2 | 3 | 4 | 5,
): Promise<EpisodeRecord | undefined> {
  const ep = await episodes.get(episodeId);
  if (!ep) return undefined;
  if (ep.endedAt) return ep;
  const updated: EpisodeRecord = {
    ...ep,
    endedAt: nowIso(),
    outcome,
    ...(intensityEnd !== undefined ? { intensityEnd } : {}),
    updatedAt: nowIso(),
  };
  await episodes.put(updated);
  return updated;
}

export function elapsedMs(ep: EpisodeRecord, nowMs: number = Date.now()): number {
  const start = new Date(ep.startedAt).getTime();
  const end = ep.endedAt ? new Date(ep.endedAt).getTime() : nowMs;
  return Math.max(0, end - start);
}
